import { type CallMedia, type CallsState, isMissedFor } from "@rhema/client";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { errorText } from "../billing/UpgradeLink";
import { useClient, useSession } from "../session/session";
import { FloatingCall } from "./FloatingCall";
import { IncomingRing } from "./IncomingRing";
import { CallRoom, e2eeSupported, type RoomSnapshot, UNSUPPORTED, unsupportedMessage } from "./media";
import { startRingtone } from "./ringtone";

export type Calls = {
  state: CallsState;
  room: CallRoom | null;
  snapshot: RoomSnapshot | null;
  /** A plain message about why a call could not start or join. */
  notice: string | null;
  unseenMissed: number;
  start: (chatId: string, media: CallMedia) => Promise<void>;
  accept: (callId: string) => Promise<void>;
  decline: (callId: string) => Promise<void>;
  end: () => Promise<void>;
  dismissNotice: () => void;
  markSeen: () => void;
};

const CallsContext = createContext<Calls | null>(null);

const SEEN_KEY = "rhema:calls-seen";

function readSeen(): string {
  try {
    return globalThis.localStorage?.getItem(SEEN_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeSeen(value: string) {
  try {
    globalThis.localStorage?.setItem(SEEN_KEY, value);
  } catch {
    // Private windows can refuse storage. The badge then resets on reload.
  }
}

const noSubscribe = () => () => {};
const noSnapshot = () => null;

export function CallProvider({ children }: { children: ReactNode }) {
  const client = useClient();
  const { platform } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const state = useSyncExternalStore(client.calls.subscribe, client.calls.getState, client.calls.getState);
  const [room, setRoom] = useState<CallRoom | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [seen, setSeen] = useState(readSeen);
  const connectedFor = useRef<string | null>(null);
  const snapshot = useSyncExternalStore(
    room?.subscribe ?? noSubscribe,
    room?.getSnapshot ?? noSnapshot,
    room?.getSnapshot ?? noSnapshot,
  );

  useEffect(() => {
    void client.callHistory().catch(() => undefined);
  }, [client]);

  // Connect the encrypted media room when a call becomes active, and close it when the call ends.
  const active = state.active;
  const roomRef = useRef<CallRoom | null>(null);
  useEffect(() => () => void roomRef.current?.disconnect(), []);
  useEffect(() => {
    if (!active) {
      connectedFor.current = null;
      void roomRef.current?.disconnect();
      roomRef.current = null;
      setRoom(null);
      return;
    }
    if (connectedFor.current === active.call.id) return;
    connectedFor.current = active.call.id;
    void roomRef.current?.disconnect();
    const next = new CallRoom();
    roomRef.current = next;
    setRoom(next);
    next
      .connect({
        url: active.livekitUrl,
        token: active.token,
        key: active.key,
        video: active.call.media === "video",
      })
      .catch((err: unknown) => {
        setNotice(
          err instanceof Error && err.message === UNSUPPORTED
            ? unsupportedMessage()
            : "Rhema could not connect the call. Check your connection and try again.",
        );
        void next.disconnect();
        void client.leaveCall();
      });
  }, [active, client]);

  // The desktop app waits for the call to end before it restarts into an update.
  const inCall = Boolean(active || state.joining);
  useEffect(() => {
    if (!inCall) return;
    platform.setInCall?.(true);
    return () => platform.setInCall?.(false);
  }, [platform, inCall]);

  // Closing or reloading the page drops the media, so leave the call on the server too.
  useEffect(() => {
    if (!active) return;
    const onExit = () => client.calls.leaveOnExit();
    window.addEventListener("pagehide", onExit);
    return () => window.removeEventListener("pagehide", onExit);
  }, [active, client]);

  // Ring while a call is waiting for an answer and this device is not already in one.
  const ringing = Boolean(state.incoming && !state.active);
  useEffect(() => {
    if (!ringing) return;
    return startRingtone();
  }, [ringing]);

  const start = useCallback(
    async (chatId: string, media: CallMedia) => {
      setNotice(null);
      if (!e2eeSupported()) {
        setNotice(unsupportedMessage());
        return;
      }
      try {
        await client.startCall(chatId, media);
      } catch (err) {
        setNotice(errorText(err, platform));
      }
    },
    [client, platform],
  );

  const accept = useCallback(
    async (callId: string) => {
      setNotice(null);
      if (!e2eeSupported()) {
        setNotice(unsupportedMessage());
        return;
      }
      try {
        const joined = await client.joinCall(callId);
        navigate(`/calls/${joined.chatId}`);
      } catch (err) {
        setNotice(errorText(err, platform));
      }
    },
    [client, navigate, platform],
  );

  const decline = useCallback(
    async (callId: string) => {
      await client.declineCall(callId).catch(() => undefined);
    },
    [client],
  );

  const end = useCallback(async () => {
    await room?.disconnect();
    await client.leaveCall();
  }, [client, room]);

  const meId = client.state.me?.id;
  const unseenMissed = state.history.filter((c) => c.started_at > seen && isMissedFor(c, meId)).length;
  const markSeen = useCallback(() => {
    const now = new Date().toISOString();
    writeSeen(now);
    setSeen(now);
  }, []);

  const value = useMemo<Calls>(
    () => ({
      state,
      room,
      snapshot,
      notice,
      unseenMissed,
      start,
      accept,
      decline,
      end,
      dismissNotice: () => setNotice(null),
      markSeen,
    }),
    [state, room, snapshot, notice, unseenMissed, start, accept, decline, end, markSeen],
  );

  const onCallScreen = active ? pathname === `/calls/${active.chatId}` : false;

  return (
    <CallsContext.Provider value={value}>
      {children}
      {active && !onCallScreen && <FloatingCall />}
      {state.incoming && !state.active && <IncomingRing call={state.incoming} />}
      {notice && !pathname.startsWith("/calls/") && !(state.incoming && !state.active) && (
        <div className="fixed inset-x-4 top-4 z-50 mx-auto flex max-w-[480px] items-start gap-3 rounded-card border border-scarlet bg-scarlet-tint px-4 py-3 text-sm text-ink shadow-float">
          <p role="alert" className="m-0 flex-1">
            {notice}
          </p>
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent font-semibold text-blue"
            onClick={() => setNotice(null)}
          >
            Dismiss
          </button>
        </div>
      )}
    </CallsContext.Provider>
  );
}

export function useCalls(): Calls {
  const calls = useContext(CallsContext);
  if (!calls) throw new Error("useCalls must be used inside CallProvider");
  return calls;
}

/** Seconds since a moment, ticking once a second. */
export function useElapsed(since: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (since === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [since]);
  return since === null ? 0 : Math.max(0, Math.floor((now - since) / 1000));
}

export function formatTimer(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = String(seconds % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}
